import { useCallback, useEffect, useRef } from 'react';

const WHEEL_ITEM_HEIGHT = 44;

/**
 * @param {object} props
 * @param {Array<string|number>} props.items
 * @param {string|number} props.value
 * @param {(value: string|number) => void} props.onChange
 * @param {(value: string|number) => string} [props.formatValue]
 */
export const ScrollWheel = ({
    items,
    value,
    onChange,
    formatValue = (item) => String(item).padStart(2, '0'),
}) => {
    const wheelRef = useRef(null);
    const isProgrammaticScroll = useRef(false);

    const scrollToValue = useCallback((nextValue, behavior = 'auto') => {
        const index = items.indexOf(nextValue);
        if (index < 0 || !wheelRef.current) return;

        isProgrammaticScroll.current = true;
        wheelRef.current.scrollTo({
            top: index * WHEEL_ITEM_HEIGHT,
            behavior,
        });

        window.setTimeout(() => {
            isProgrammaticScroll.current = false;
        }, behavior === 'smooth' ? 220 : 0);
    }, [items]);

    useEffect(() => {
        scrollToValue(value);
    }, [value, scrollToValue]);

    const handleScroll = () => {
        if (!wheelRef.current || isProgrammaticScroll.current) return;

        const index = Math.round(wheelRef.current.scrollTop / WHEEL_ITEM_HEIGHT);
        const clamped = Math.max(0, Math.min(index, items.length - 1));
        const nextValue = items[clamped];

        if (nextValue !== value) {
            onChange(nextValue);
        }
    };

    const handleItemClick = (item) => {
        onChange(item);
        scrollToValue(item, 'smooth');
    };

    return (
        <div className="time-picker__wheel-column">
            <div
                ref={wheelRef}
                className="time-picker__wheel"
                onScroll={handleScroll}
            >
                <div className="time-picker__wheel-spacer" aria-hidden="true" />
                {items.map((item) => (
                    <button
                        key={item}
                        type="button"
                        className="time-picker__wheel-item"
                        data-selected={item === value}
                        onClick={() => handleItemClick(item)}
                    >
                        {formatValue(item)}
                    </button>
                ))}
                <div className="time-picker__wheel-spacer" aria-hidden="true" />
            </div>
        </div>
    );
};
